// ============================================================================
// CONFIG GROUP SIDEBAR
// ============================================================================
// Left navigation panel showing config groups (TIMESHEET_POLICY, ENGAGEMENT_POLICY, SYSTEM_DEFAULTS, ...).
// Ported from crs-web/src/components/modules/config-module/ConfigGroupSidebar.tsx.

import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ConfigGroup } from 'api/types';

interface ConfigGroupSidebarProps {
  groups: ConfigGroup[];
  activeGroupKey: string | null;
  onGroupSelect: (groupKey: string) => void;
  loading?: boolean;
}

// Map DB icon names (single-word, lowercase) to IconSystem keys (camelCase).
// Names not found here fall back gracefully (no icon rendered).
const ICON_MAP: Record<string, keyof typeof IconSystem> = {
  clock: 'clock',
  briefcase: 'file',          // briefcase not in IconSystem — use file
  globe: 'home',              // globe not in IconSystem — use home as proxy
  user: 'user',
  users: 'users',
  calendar: 'calendar',
  file: 'file',
  lock: 'lock',
  tag: 'tag',
  home: 'home',
  grid: 'grid',
  chart: 'chart',
  settings: 'settings',
};

const ConfigGroupSidebar: React.FC<ConfigGroupSidebarProps> = ({
  groups,
  activeGroupKey,
  onGroupSelect,
  loading,
}) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, groupKey: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onGroupSelect(groupKey);
      }
    },
    [onGroupSelect]
  );

  if (loading) {
    return (
      <div className="config-group-sidebar">
        <div className="config-loading">Loading groups...</div>
      </div>
    );
  }

  return (
    <div className="config-group-sidebar">
      {groups.map((group) => {
        const iconKey = ICON_MAP[(group.icon ?? '').toLowerCase()];
        const Icon = iconKey ? (IconSystem as any)[iconKey] : null;

        return (
          <div
            key={group.groupKey}
            role="button"
            tabIndex={0}
            className={`config-group-item${
              activeGroupKey === group.groupKey ? ' active' : ''
            }`}
            onClick={() => onGroupSelect(group.groupKey)}
            onKeyDown={(e) => handleKeyDown(e, group.groupKey)}
          >
            {Icon && (
              <span className="config-group-icon">
                <Icon size={18} />
              </span>
            )}
            <span>{group.displayName}</span>
          </div>
        );
      })}
    </div>
  );
};

export default ConfigGroupSidebar;
